import type { AlertBannerStatus } from '@apollo/orbit';
import { AlertBanner, Link } from '@apollo/orbit';
import React, { useEffect, useState } from 'react';
import type {
  StatusPageIncidentStatus,
  StatusPageOngoingIncident,
} from 'typings/StatusPageScheduledMaintenance';

import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { useLocalStorage } from 'src/hooks/useLocalStorage';

// Resolved incidents drop out of the widget API, so we remember the ones this
// browser has seen and announce their resolution for this long afterwards.
const RESOLVED_NOTICE_WINDOW_MS = 24 * 60 * 60 * 1000;

const STATUS_LABELS: Record<StatusPageIncidentStatus, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring a fix',
};

// Dismissals are per update, so a status change or new message re-shows the
// banner even after a user has dismissed an earlier update.
const getIncidentUpdateKey = (incident: StatusPageOngoingIncident) =>
  `${incident.id}:${incident.last_update_at}`;

export const OngoingIncidents = ({
  incidents,
}: {
  /** `undefined` while the status page hasn't loaded yet */
  incidents: ReadonlyArray<StatusPageOngoingIncident> | undefined;
}) => {
  const [dismissedIncidentUpdateIds, setDismissedIncidentUpdateIds] =
    useLocalStorage('dismissedIncidentUpdateIds');
  const [seenIncidents, setSeenIncidents] = useLocalStorage('seenIncidents');
  // Stale entries are also pruned on every poll, so this needn't stay current
  const [mountedAt] = useState(Date.now);

  useEffect(() => {
    if (!incidents) return;
    const now = Date.now();
    setSeenIncidents((previous) => {
      const next = Object.fromEntries(
        Object.entries(previous).filter(
          ([, { lastSeenAt }]) =>
            now - new Date(lastSeenAt).getTime() < RESOLVED_NOTICE_WINDOW_MS,
        ),
      );
      incidents.forEach(({ id, name, url }) => {
        next[id] = { id, name, url, lastSeenAt: new Date(now).toISOString() };
      });
      return next;
    });
  }, [incidents, setSeenIncidents]);

  if (!incidents) {
    return null;
  }

  const ongoingIds = new Set(incidents.map(({ id }) => id));
  const ongoingToShow = incidents.filter(
    (incident) =>
      !dismissedIncidentUpdateIds.includes(getIncidentUpdateKey(incident)),
  );
  const resolvedToShow = Object.values(seenIncidents).filter(
    ({ id, lastSeenAt }) =>
      !ongoingIds.has(id) &&
      mountedAt - new Date(lastSeenAt).getTime() < RESOLVED_NOTICE_WINDOW_MS,
  );

  if (ongoingToShow.length === 0 && resolvedToShow.length === 0) {
    return null;
  }

  const status: AlertBannerStatus = ongoingToShow.some(
    (incident) => incident.current_worst_impact === 'full_outage',
  )
    ? 'error'
    : ongoingToShow.length > 0
      ? 'warning'
      : 'info';
  const shortenBodies = ongoingToShow.length + resolvedToShow.length > 1;

  return (
    <AlertBanner
      status={status}
      isDismissible
      onDismiss={() => {
        setDismissedIncidentUpdateIds([
          ...ongoingToShow.map(getIncidentUpdateKey),
          // Keys for incidents that are no longer ongoing can't match again
          ...dismissedIncidentUpdateIds.filter((key) =>
            ongoingIds.has(key.slice(0, key.indexOf(':'))),
          ),
        ]);
        const resolvedIds = new Set(resolvedToShow.map(({ id }) => id));
        setSeenIncidents((previous) =>
          Object.fromEntries(
            Object.entries(previous).filter(([id]) => !resolvedIds.has(id)),
          ),
        );
      }}
    >
      {ongoingToShow.map((incident) => {
        const bodyText = incident.last_update_message;
        const showBody = !shortenBodies && bodyText && bodyText.length <= 180;

        return (
          <React.Fragment key={incident.id}>
            <div>
              <span className="font-semibold">
                Ongoing incident (
                {STATUS_LABELS[incident.status] ?? incident.status}):
              </span>{' '}
              {incident.name}. Updated{' '}
              <LocalTimestamp
                value={new Date(incident.last_update_at)}
                className="font-semibold"
              />
              .{' '}
              <Link isExternal to={incident.url}>
                Learn more
              </Link>
            </div>
            {showBody && <p>{bodyText}</p>}
          </React.Fragment>
        );
      })}
      {resolvedToShow.map((incident) => (
        <div key={incident.id}>
          <span className="font-semibold">Resolved:</span> {incident.name}.{' '}
          <Link isExternal to={incident.url}>
            Learn more
          </Link>
        </div>
      ))}
    </AlertBanner>
  );
};
